import type { Dispatch, SetStateAction } from 'react';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import useGoogleAnalytics from '@/hooks/analytics/useGoogleAnalytics';
import { useResetProductQuantitiesOnMount, useSelectedProductQuantities } from '@/hooks/products/useProductQuantity';
import { UNKNOWN_SUPPLIER_NAME, getIssueAndDispenseDateForProduct } from '@/pages/umeds/order-medication/util';
import { getPatientDiscountCreditLineItems } from '@/services/patientCredit.service';
import { Logger } from '@/utils/logger';
import type { ErrorDisplay } from '@montugroup/circuit-shared/context/ErrorManagement';
import { useErrorManagement } from '@montugroup/circuit-shared/context/ErrorManagement';
import { PreferencesContext } from '@montugroup/circuit-shared/context/Preferences';
import { PaymentService } from '@montugroup/circuit-shared/services/payment.service';
import type { Price } from '@montugroup/circuit-shared/types/index';
import { GoogleAnalyticsEventName } from '@montugroup/circuit-shared/types/index';

import { PATIENT_REFILL_PHARMACY_ROUTE } from '@/routes/route-paths';
import { getTsApiV1Client } from '@montugroup/circuit-shared/api/api-v1.client';
import useUser from '@montugroup/circuit-shared/hooks/user/useUser';
import type {
  AsyncContent,
  CreditLineItem,
  RefillResponse,
  RefillResponseProduct,
} from '@montugroup/circuit-shared/types/refill';
import type { PersistedProductDetails } from './persistedOrderDetails';
import { managePersistedOrderDetails } from './persistedOrderDetails';
import { FORMULATION_ID_DEVICE, nullToUndefined, remapResponseRemainingUnits } from './util';

const logger = new Logger('OrderMedicationHook');

const client = getTsApiV1Client();

export interface SelectedProductData {
  quantity_original: number;
  product_name: string;
  repeats: number;
  price: Price;
  remaining_units: number;
  interval: number;
  is_out_of_stock: boolean;
  short_name: string | null;
  is_concession: boolean;
  brand: string;
  needsApproval: boolean;
}

export interface SelectedProduct {
  id: number;
  data: SelectedProductData & {
    prescription_id?: number;
    product_id: number;
    quantity: number;
  };
}

export type ConfirmationPromise<T> = (data: T) => Promise<boolean>;
type PlaceOrderFn = () => Promise<void>;
type ValidateOrderFn = (placeOrderCallback: PlaceOrderFn) => Promise<void>;

export const generateSelectedProducts = (
  productSelection: Map<number, number>,
  products: RefillResponseProduct[],
): SelectedProduct[] => {
  const selectedProducts: SelectedProduct[] = [];
  productSelection.forEach((quantity, productId) => {
    const product = products.find((prd) => prd.id === productId);
    if (!product || quantity === 0) {
      return;
    }
    selectedProducts.push({
      id: productId,
      data: {
        ...product,
        remaining_units: product.remaining_units!,
        prescription_id: product.prescription_id,
        product_name: product.name,
        quantity_original: product.quantity,
        quantity,
        product_id: product.id,
        repeats: product.repeats || 0,
        interval: product.interval || 0,
        short_name: product.short_name || null,
        brand: product.brand ?? UNKNOWN_SUPPLIER_NAME,
        needsApproval: product.needSASApproval || false,
      },
    });
  });
  return selectedProducts;
};

const generatePersistedProductDetails = (
  productSelection: Map<number, number>,
  products: RefillResponseProduct[],
): PersistedProductDetails[] => {
  const selectedProducts: PersistedProductDetails[] = [];
  productSelection.forEach((quantity, productId) => {
    const product = products.find((prd) => prd.id === productId);
    if (!product || quantity === 0) {
      return;
    }
    selectedProducts.push({
      id: product.id,
      name: product.name,
      quantity,
      price: product.price,
      brand: product.brand ?? UNKNOWN_SUPPLIER_NAME,
      is_concession: Boolean(product.is_concession),
      ...getIssueAndDispenseDateForProduct(product),
    });
  });
  return selectedProducts;
};

export const determineIfNeedSasApproval = (products: SelectedProduct[]): boolean => {
  return products.some((product) => product.data.needsApproval) || false;
};

export interface OrderMedicationController {
  state: {
    refillResponse: AsyncContent<RefillResponse>;
    isSubmitting: boolean;
    selectedProducts: SelectedProduct[];
    hasSelectedAnyProducts: boolean;
    creditDiscounts: CreditLineItem[];
    needSasApproval?: boolean;
    // These are exposed for unit testing, but shouldn't be accessed in production code
    __testing: {
      productSelection: Map<number, number>;
    };
  };

  actions: {
    submitOrder: (params: { validateOrderCallback: ValidateOrderFn }) => Promise<void>;
    validateOrder: ValidateOrderFn;
    enqueueError: (error: ErrorDisplay) => void;

    // These are exposed for unit testing, but should never be called
    __testing: {
      setIsSubmitting: Dispatch<SetStateAction<boolean>>;
      placeOrderShopify: () => Promise<void>;
    };
  };
}

export const useOrderMedicationController = (): OrderMedicationController => {
  const { data: user } = useUser();
  const navigate = useNavigate();
  const { enqueueError } = useErrorManagement();
  const { sendGoogleAnalyticsGTagEvent } = useGoogleAnalytics();
  const persistedOrderDetails = managePersistedOrderDetails();

  const productSelection = useSelectedProductQuantities();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { umedsMarketingConsent } = useContext(PreferencesContext);

  /**
   * ***********
   * Fetch data from Circuit Service
   ************
   */
  const getPatientRefillResponse = client.order.getPatientRefill.useQuery({
    queryKey: client.order.getPatientRefill.keys,
    queryData: {
      query: {},
      params: {
        userId: Number(user!.id),
      },
    },
    enabled: !!user?.id && !Number.isNaN(Number(user.id)),
    meta: {
      error: {
        message: 'Page load error',
        log: 'Call to fetch patient refill data failed',
        showToast: true,
      },
    },
  });

  /**
   * *************
   * Derived State
   **************
   */

  const refillAPIData = useMemo(
    () => getPatientRefillResponse.data?.body && remapResponseRemainingUnits(getPatientRefillResponse.data.body),
    [getPatientRefillResponse.data?.body],
  );

  const refillResponse = useMemo(() => {
    return {
      ...getPatientRefillResponse,
      data: refillAPIData,
    };
  }, [getPatientRefillResponse, refillAPIData]);

  const selectedProducts = generateSelectedProducts(productSelection, refillAPIData?.products || []);

  const needSasApproval = determineIfNeedSasApproval(selectedProducts);

  const hasSelectedAnyProducts = selectedProducts.filter((x) => x.data.product_id !== null).length > 0;

  const creditDiscounts = !refillAPIData
    ? []
    : getPatientDiscountCreditLineItems({
        selectedProducts,
        patientDiscounts: refillAPIData?.patientDiscounts,
      });

  useResetProductQuantitiesOnMount();

  // Persist the current product selection for the downstream shipping/delivery pages.
  // Nothing reads it back into the quantity store on load: order pre-filling was
  // removed on both customer types (#7647), so the page always starts empty.
  useEffect(() => {
    const persistedProductDetails = generatePersistedProductDetails(productSelection, refillAPIData?.products || []);
    persistedOrderDetails.setOrderDetails(persistedProductDetails, creditDiscounts, needSasApproval);
  }, [productSelection]);

  /**
   * *********
   * Callbacks
   *********
   */

  const placeOrderShopify = useCallback(async () => {
    const { order } = refillAPIData!;

    const productsPayload = selectedProducts
      .filter((selectedProd) => {
        const product = refillAPIData!.products.find(({ id }) => id === selectedProd.data.product_id);
        return (product && product.remaining_units! > 0) || product?.formulation_id === FORMULATION_ID_DEVICE;
      })
      .map(({ data }) => ({
        quantity: data.quantity,
        productId: data.product_id,
      }));

    const { checkoutRedirectUrl, error: responseError } = await PaymentService.getCheckoutRedirectUrl(
      productsPayload,
      order?.id,
      nullToUndefined(order?.order_code),
      // TODO: to avoid empty arguments like this, `getCheckoutRedirectUrl`
      // should be refactored to accept an object
      // eslint-disable-next-line no-undefined
      undefined,
      umedsMarketingConsent,
    );

    if (checkoutRedirectUrl) {
      window.location.replace(checkoutRedirectUrl);
    } else {
      setIsSubmitting(false);
      logger.error(responseError || 'Failed to retrieve checkoutUrl');
      throw new Error('Something went wrong, please try again');
    }
  }, [refillAPIData, selectedProducts]);

  // Last chance to stop order submission, perform validation logic here.
  const validateOrder = useCallback(
    async (placeOrderCallback: PlaceOrderFn): Promise<void> => {
      try {
        setIsSubmitting(true);
        await placeOrderCallback();
      } catch (error) {
        logger.error(error);
        setIsSubmitting(false);
        throw error;
      }
    },
    [setIsSubmitting, selectedProducts],
  );

  const submitOrder = async (params: { validateOrderCallback: ValidateOrderFn }) => {
    const { validateOrderCallback } = params;

    if (isSubmitting || !refillAPIData) {
      return;
    }

    const { order } = refillAPIData;

    // Send event via Google Analytics
    sendGoogleAnalyticsGTagEvent(GoogleAnalyticsEventName.CLICK_CTA, {
      link_id: 'cart_checkout_button',
    });

    await validateOrderCallback(async () => {
      navigate(
        {
          pathname: PATIENT_REFILL_PHARMACY_ROUTE,
        },
        {
          state: { orderId: order?.id, orderCode: order?.order_code, uMedsMarketingConsent: !!umedsMarketingConsent },
        },
      );
    });
  };

  return {
    state: {
      refillResponse,
      isSubmitting,
      selectedProducts,
      hasSelectedAnyProducts,
      creditDiscounts,
      needSasApproval,
      // These are exposed for unit testing, but shouldn't be accessed in production code
      __testing: {
        productSelection,
      },
    },

    actions: {
      submitOrder,
      validateOrder,
      enqueueError,

      // These are exposed for unit testing, but should never be called
      __testing: {
        setIsSubmitting,
        placeOrderShopify,
      },
    },
  };
};

export default useOrderMedicationController;
